import { Copy, Plus, Settings, Trash2 } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';
import { useReactFlow } from 'reactflow';
import { useIsDark } from '../../contexts/ThemeContext';
import { useNodeValidation, useNodeValidationPattern } from '../../hooks/useNodeValidation';
import { ValidationResultDisplay } from '../shared/ValidationResultDisplay';
import { getAvailableFields } from '../../lib/workflow-utils';
import { IntegrationsNodeData } from '../../types/nodes';
import { VariablePicker } from '../VariablePicker';

/**
 * Extract value from object using dot notation path
 */
const extractValueFromPath = (obj: any, path: string): any => {
  const parts = path.split('.');
  let current = obj;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return undefined;
    }
  }

  return current;
};

interface HubSpotConfigProps {
  nodeId: string;
  data: IntegrationsNodeData;
  onChange: (data: IntegrationsNodeData) => void;
  onWizardStateChange?: (state: {
    wizardMode: boolean;
    currentStep: number;
    totalSteps: number;
    canProceedToNext: boolean;
    onPreviousStep: () => void;
    onNextStep: () => void;
    onCompleteWizard: () => Promise<void>;
    validationAttempted: boolean;
    validationFailed: boolean;
  }) => void;
  onValidationRequest?: (validate: () => Promise<boolean>) => void;
}

interface HubSpotConfig {
  // Step 1: API & Operation
  apiType: string;
  operation: string;
  endpoint?: string;
  method?: string;
  
  // Step 2: Request Configuration
  properties: Record<string, string>; // HubSpot property -> token mapping
  formId?: string;
  objectId?: string;
  filterGroups?: any[];
  sorts?: any[];
  limit?: number;
  searchProperties?: string[];
  customHeaders?: Record<string, string>;
  customBody?: string;
  
  // Search Configuration Fields
  dealStage?: string;
  minAmount?: string;
  maxAmount?: string;
  closeDateFilter?: string;
  closeDateFrom?: string;
  closeDateTo?: string;
  sortField?: string;
  sortDirection?: string;
  
  // Step 3: Response Handling
  storeAs: string;
  extractPath?: string;
  responseFields: { key: string; path: string; type: string }[]; // Fields to extract from response
  
  // Advanced
  retryConfig?: {
    maxAttempts: number;
    delay: number;
  };
  timeout?: number;
  
  // Legacy
  matchField?: string;
  fieldMappings?: Record<string, string>;
}



interface PropertyMapping {
  hubspotProperty: string;
  tokenValue: string;
  resolvedPreview?: string;
}

const defaultConfig: HubSpotConfig = {
  apiType: 'contacts',
  operation: 'create',
  properties: {},
  storeAs: '',
  method: 'POST',
  responseFields: []
};

// API Type configurations
type OperationConfig = {
  label: string;
  method: string;
  endpoint: string;
};

type ApiTypeConfig = {
  label: string;
  operations: Record<string, OperationConfig>;
  properties: Array<{
    name: string;
    label: string;
    required: boolean;
    type: string;
  }>;
};

const HUBSPOT_API_TYPES: Record<string, ApiTypeConfig> = {
  contacts: {
    label: 'Contacts',
    operations: {
      create: { label: 'Create Contact', method: 'POST', endpoint: '/crm/v3/objects/contacts' },
      update: { label: 'Update Contact', method: 'PATCH', endpoint: '/crm/v3/objects/contacts/{objectId}' },
      search: { label: 'Search Contacts', method: 'POST', endpoint: '/crm/v3/objects/contacts/search' },
      get: { label: 'Get Contact', method: 'GET', endpoint: '/crm/v3/objects/contacts/{objectId}' }
    },
    properties: [
      { name: 'email', label: 'Email', required: true, type: 'string' },
      { name: 'firstname', label: 'First Name', required: false, type: 'string' },
      { name: 'lastname', label: 'Last Name', required: false, type: 'string' },
      { name: 'phone', label: 'Phone', required: false, type: 'string' },
      { name: 'company', label: 'Company', required: false, type: 'string' },
      { name: 'jobtitle', label: 'Job Title', required: false, type: 'string' },
      { name: 'website', label: 'Website', required: false, type: 'string' },
      { name: 'city', label: 'City', required: false, type: 'string' },
      { name: 'state', label: 'State', required: false, type: 'string' },
      { name: 'country', label: 'Country', required: false, type: 'string' }
    ]
  },
  companies: {
    label: 'Companies',
    operations: {
      create: { label: 'Create Company', method: 'POST', endpoint: '/crm/v3/objects/companies' },
      update: { label: 'Update Company', method: 'PATCH', endpoint: '/crm/v3/objects/companies/{objectId}' },
      search: { label: 'Search Companies', method: 'POST', endpoint: '/crm/v3/objects/companies/search' },
      get: { label: 'Get Company', method: 'GET', endpoint: '/crm/v3/objects/companies/{objectId}' }
    },
    properties: [
      { name: 'name', label: 'Company Name', required: true, type: 'string' },
      { name: 'domain', label: 'Domain', required: false, type: 'string' },
      { name: 'phone', label: 'Phone', required: false, type: 'string' },
      { name: 'industry', label: 'Industry', required: false, type: 'string' },
      { name: 'city', label: 'City', required: false, type: 'string' },
      { name: 'state', label: 'State', required: false, type: 'string' },
      { name: 'country', label: 'Country', required: false, type: 'string' },
      { name: 'founded_year', label: 'Founded Year', required: false, type: 'number' },
      { name: 'num_employees', label: 'Number of Employees', required: false, type: 'number' }
    ]
  },
  deals: {
    label: 'Deals',
    operations: {
      create: { label: 'Create Deal', method: 'POST', endpoint: '/crm/v3/objects/deals' },
      update: { label: 'Update Deal', method: 'PATCH', endpoint: '/crm/v3/objects/deals/{objectId}' },
      search: { label: 'Search Deals', method: 'POST', endpoint: '/crm/v3/objects/deals/search' },
      get: { label: 'Get Deal', method: 'GET', endpoint: '/crm/v3/objects/deals/{objectId}' }
    },
    properties: [
      { name: 'dealname', label: 'Deal Name', required: true, type: 'string' },
      { name: 'amount', label: 'Amount', required: false, type: 'number' },
      { name: 'closedate', label: 'Close Date', required: false, type: 'date' },
      { name: 'pipeline', label: 'Pipeline', required: false, type: 'string' },
      { name: 'dealstage', label: 'Deal Stage', required: false, type: 'string' },
      { name: 'dealtype', label: 'Deal Type', required: false, type: 'string' }
    ]
  },
  forms: {
    label: 'Forms',
    operations: {
      submit: { label: 'Submit Form', method: 'POST', endpoint: '/submissions/v3/integration/submit/{formId}' }
    },
    properties: []
  },
  tickets: {
    label: 'Tickets',
    operations: {
      create: { label: 'Create Ticket', method: 'POST', endpoint: '/crm/v3/objects/tickets' },
      update: { label: 'Update Ticket', method: 'PATCH', endpoint: '/crm/v3/objects/tickets/{objectId}' },
      search: { label: 'Search Tickets', method: 'POST', endpoint: '/crm/v3/objects/tickets/search' },
      get: { label: 'Get Ticket', method: 'GET', endpoint: '/crm/v3/objects/tickets/{objectId}' }
    },
    properties: [
      { name: 'subject', label: 'Subject', required: true, type: 'string' },
      { name: 'content', label: 'Content', required: true, type: 'string' },
      { name: 'hs_pipeline', label: 'Pipeline', required: false, type: 'string' },
      { name: 'hs_pipeline_stage', label: 'Pipeline Stage', required: false, type: 'string' },
      { name: 'hs_ticket_priority', label: 'Priority', required: false, type: 'string' }
    ]
  },
  custom: {
    label: 'Custom API',
    operations: {
      custom: { label: 'Custom Request', method: 'POST', endpoint: '' }
    },
    properties: []
  }
};

export function HubSpotConfig({ nodeId, data, onChange, onWizardStateChange, onValidationRequest }: HubSpotConfigProps) {
	const { validateNode, isValidating, lastValidationResult, clearValidationResult } = useNodeValidation();
	
	// Validation state for two-step complete setup
	const [validationAttempted, setValidationAttempted] = useState(false); // Track if validation has been attempted
	const [validationFailed, setValidationFailed] = useState(false); // Track if validation failed on previous attempt

  const { getNodes, getEdges } = useReactFlow();
  const isDark = useIsDark();
  const [config, setConfig] = useState<HubSpotConfig>(() => {
    const existingConfig = data.config as Partial<HubSpotConfig>;
    return {
      ...defaultConfig,
      ...existingConfig,
      storeAs: existingConfig?.storeAs || nodeId
    };
  });

  const [currentStep, setCurrentStep] = useState(1);

  const [propertyMappings, setPropertyMappings] = useState<PropertyMapping[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [isWizardCompleted, setIsWizardCompleted] = useState(false);
  const [showJsonPaste, setShowJsonPaste] = useState(false);
  const [jsonPasteText, setJsonPasteText] = useState('');
  const [jsonPasteError, setJsonPasteError] = useState('');
  const [jsonPreviewFields, setJsonPreviewFields] = useState<Array<{ key: string; path: string; type: string }>>([]);

  // Get available upstream fields
  const getUpstreamFields = () => {
    const nodes = getNodes();
    const edges = getEdges();
    const availableFields = getAvailableFields(nodeId, nodes, edges);
    return availableFields.flatMap(context => context.fields);
  };

  // Get current API configuration
  const getCurrentApiConfig = () => {
    return HUBSPOT_API_TYPES[config.apiType as keyof typeof HUBSPOT_API_TYPES];
  };

  // Get current operation configuration
  const getCurrentOperationConfig = () => {
    const apiConfig = getCurrentApiConfig();
    if (!apiConfig || !apiConfig.operations) return null;
    return apiConfig.operations[config.operation as keyof typeof apiConfig.operations] || null;
  };

  // Auto-generate endpoint and method when API type/operation changes
  useEffect(() => {
    const operationConfig = getCurrentOperationConfig();
    if (operationConfig && operationConfig.endpoint && operationConfig.method) {
      setConfig(prev => ({
        ...prev,
        endpoint: operationConfig.endpoint,
        method: operationConfig.method
      }));
    }
    // Clear property mappings when API type changes since available properties change
    setPropertyMappings([]);
  }, [config.apiType, config.operation]);

  // Initialize property mappings from current config (only on component mount)
  useEffect(() => {
    const existingConfig = data.config as Partial<HubSpotConfig>;
    const mappings = Object.entries(existingConfig?.properties || {}).map(([hubspotProperty, tokenValue]) => ({
      hubspotProperty,
      tokenValue,
      resolvedPreview: ''
    }));
    setPropertyMappings(mappings);
  }, []); // Only run on mount to avoid resetting during user interaction

  // Clear preview fields when JSON text changes
  useEffect(() => {
    if (jsonPasteText.trim()) {
      setJsonPreviewFields([]);
    }
  }, [jsonPasteText]);



  // Step validation
  const canProceedToStep = (step: number): boolean => {
    switch (step) {
      case 1: return true;
      case 2: return Boolean(config.apiType && config.operation);
      case 3: {
        // For search operations, we don't need property mappings
        if (config.operation === 'search') {
          return true; // Search operations can proceed without property mappings
        }
        // For get operations, we don't need property mappings
        if (config.operation === 'get') {
          return true;
        }
        // For other operations, we need at least one property mapping
        return Object.keys(config.properties || {}).length > 0;
      }
      
      default: return false;
    }
  };

  // Update configuration
  const handleConfigChange = (updates: Partial<HubSpotConfig>) => {
    const updatedConfig = { ...config, ...updates };
    setConfig(updatedConfig);
    onChange({
      ...data,
      config: updatedConfig,
    });
  };

  // Add property mapping
  const addPropertyMapping = () => {
    setPropertyMappings(prev => [...prev, {
      hubspotProperty: '',
      tokenValue: '',
      resolvedPreview: ''
    }]);
  };

  // Update property mapping
  const updatePropertyMapping = (index: number, updates: Partial<PropertyMapping>) => {
    const newMappings = [...propertyMappings];
    newMappings[index] = { ...newMappings[index], ...updates };
    
    // Update preview if token value changed
    if (updates.tokenValue !== undefined) {
      newMappings[index].resolvedPreview = updates.tokenValue ? '' : '';
    }
    
    setPropertyMappings(newMappings);
    
    // Update config properties - preserve existing properties and only update complete mappings
    const properties: Record<string, string> = { ...config.properties };
    
    // If this mapping is complete, add/update it
    if (newMappings[index].hubspotProperty && newMappings[index].tokenValue) {
      properties[newMappings[index].hubspotProperty] = newMappings[index].tokenValue;
    }
    
    // Remove any properties that no longer have complete mappings
    const validMappings = newMappings.filter(m => m.hubspotProperty && m.tokenValue);
    const validPropertyNames = new Set(validMappings.map(m => m.hubspotProperty));
    
    // Clean up properties that are no longer mapped
    Object.keys(properties).forEach(propertyName => {
      if (!validPropertyNames.has(propertyName)) {
        delete properties[propertyName];
      }
    });
    
    handleConfigChange({ properties });
  };

  // Remove property mapping
  const removePropertyMapping = (index: number) => {
    const mappingToRemove = propertyMappings[index];
    const newMappings = propertyMappings.filter((_, i) => i !== index);
    setPropertyMappings(newMappings);
    
    // Remove the specific property from config if it was mapped
    const properties = { ...config.properties };
    if (mappingToRemove.hubspotProperty && properties[mappingToRemove.hubspotProperty]) {
      delete properties[mappingToRemove.hubspotProperty];
    }
    
    // Ensure all remaining valid mappings are in the properties
    newMappings.forEach(mapping => {
      if (mapping.hubspotProperty && mapping.tokenValue) {
        properties[mapping.hubspotProperty] = mapping.tokenValue;
      }
    });
    
    handleConfigChange({ properties });
  };

  // Extract fields from pasted JSON
  const extractFieldsFromJson = () => {
    try {
      setJsonPasteError('');
      const jsonData = JSON.parse(jsonPasteText);
      
      // Extract all possible paths from the JSON
      const extractedFields = extractAllPaths(jsonData);
      
      // Convert to response field format
      const responseFields = extractedFields.map(field => ({
        key: field.key,
        path: field.path,
        type: field.type
      }));
      
      // Update the config with extracted fields
      handleConfigChange({ responseFields });
      
      // Clear the JSON paste text
      setJsonPasteText('');
      setShowJsonPaste(false);
      
    } catch (error) {
      setJsonPasteError('Invalid JSON format. Please check your input.');
    }
  };

  // Extract all possible paths from a JSON object
  const extractAllPaths = (obj: any, prefix = ''): Array<{ key: string; path: string; type: string }> => {
    const paths: Array<{ key: string; path: string; type: string }> = [];
    
    if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
      Object.entries(obj).forEach(([key, value]) => {
        const currentPath = prefix ? `${prefix}.${key}` : key;
        const keyName = key.replace(/[^a-zA-Z0-9_]/g, '_');
        
        // Determine the type
        let type = 'string';
        if (typeof value === 'number') type = 'number';
        else if (typeof value === 'boolean') type = 'boolean';
        else if (Array.isArray(value)) type = 'array';
        else if (value && typeof value === 'object') type = 'object';
        
        // Add this path
        paths.push({
          key: keyName,
          path: currentPath,
          type
        });
        
        // Recursively extract nested paths (but limit depth to avoid too many fields)
        if (value && typeof value === 'object' && !Array.isArray(value) && prefix.split('.').length < 3) {
          paths.push(...extractAllPaths(value, currentPath));
        }
      });
    } else if (Array.isArray(obj) && obj.length > 0) {
      // Handle arrays - extract first item as example
      const firstItem = obj[0];
      if (firstItem && typeof firstItem === 'object') {
        paths.push(...extractAllPaths(firstItem, `${prefix}[0]`));
      }
    }
    
    return paths;
  };

  // Preview extracted fields from JSON without applying them
  const previewExtractedFields = () => {
    try {
      setJsonPasteError('');
      const jsonData = JSON.parse(jsonPasteText);
      
      // Extract all possible paths from the JSON
      const extractedFields = extractAllPaths(jsonData);
      
      // Set the preview fields
      setJsonPreviewFields(extractedFields);
      
    } catch (error) {
      setJsonPasteError('Invalid JSON format. Please check your input.');
      setJsonPreviewFields([]);
    }
  };

  

	// Validation function that can be called by parent components
	const performValidation = useCallback(async (): Promise<boolean> => {
		try {
			console.log('🔍 Starting validation for HubSpot node:', nodeId);
			
			// For search operations, we can skip some validations since they don't need property mappings
			if (config.operation === 'search') {
				console.log('🔍 Search operation detected - skipping property mapping validation');
				// Basic validation: just check if we have the essential fields
				if (!config.apiType || !config.operation || !config.storeAs.trim()) {
					console.log('❌ Search operation missing essential fields');
					return false;
				}
				console.log('✅ Search operation validation passed');
				return true;
			}
			
			// For other operations, run full validation
			const result = await validateNode(nodeId, data);
			console.log('✅ HubSpot validation result:', result);
			
			// Return true if validation passed, false if there are critical errors
			return result.valid || !result.errors.some(error => error.severity === 'error');
		} catch (error) {
			console.error('❌ HubSpot validation failed:', error);
			return false;
		}
	}, [validateNode, nodeId, data, config.operation, config.apiType, config.storeAs]);

	// Register validation function with parent
	useEffect(() => {
		if (onValidationRequest) {
			onValidationRequest(performValidation);
		}
	}, [onValidationRequest, performValidation]);

	// Wizard state management
  useEffect(() => {
    if (onWizardStateChange) {
      onWizardStateChange({
        wizardMode: !isWizardCompleted,
        currentStep,
                 totalSteps: 3,
        canProceedToNext: canProceedToStep(currentStep + 1),
        onPreviousStep: () => setCurrentStep(Math.max(1, currentStep - 1)),
        onNextStep: () => setCurrentStep(currentStep + 1),
        onCompleteWizard: async () => {
          console.log('🚀 HubSpot: onCompleteWizard called');
          
          // Step 1: If validation hasn't been attempted yet, run validation and stay in node
          if (!validationAttempted) {
            try {
              console.log('🔍 Starting validation for HubSpot node:', nodeId);
              
              // Use the existing validation function
              const isValid = await performValidation();
              console.log('✅ Validation function completed, result:', isValid);
              
              setValidationAttempted(true);
              console.log('🔄 Set validationAttempted to true');
              
              if (!isValid) {
                // Validation failed - stay in node and show errors
                setValidationFailed(true);
                console.log('❌ HubSpot validation failed, staying in node');
                
                // Show validation errors in the UI
                if (lastValidationResult?.errors && lastValidationResult.errors.length > 0) {
                  console.log('📋 Validation errors:', lastValidationResult.errors);
                }
                
                // IMPORTANT: Don't exit wizard mode, keep it open
                console.log('🔄 Keeping wizard open due to validation failure');
                return; // Don't exit the node
              } else {
                // Validation succeeded - proceed to exit
                setValidationFailed(false);
                console.log('✅ HubSpot validation succeeded');
              }
            } catch (error) {
              console.error('❌ HubSpot validation failed with error:', error);
              setValidationAttempted(true);
              setValidationFailed(true);
              
              // Show error message to user
              console.log('💥 Validation error occurred, staying in node');
              return; // Don't exit the node on error
            }
          }
          
          // Step 2: If validation was already attempted and succeeded, allow exit
          if (validationAttempted && !validationFailed) {
            console.log('🔄 Validation already succeeded, allowing exit');
            
            // Save the current configuration and exit wizard mode
            setIsWizardCompleted(true);
            // Don't reset currentStep immediately - let BaseNode close drawer first
            setTimeout(() => setCurrentStep(1), 100);
            
            // Reset validation state for next time
            setValidationAttempted(false);
            setValidationFailed(false);
          } else if (validationAttempted && validationFailed) {
            // Step 3: If validation failed but user wants to proceed anyway
            console.log('🔄 Validation failed but user wants to proceed anyway');
            
            // Save the current configuration and exit wizard mode
            setIsWizardCompleted(true);
            // Don't reset currentStep immediately - let BaseNode close drawer first
            setTimeout(() => setCurrentStep(1), 100);
            
            // Reset validation state for next time
            setValidationAttempted(false);
            setValidationFailed(false);
          }
        },
        validationAttempted,
        validationFailed,
      });
    }
  }, [currentStep, config, onWizardStateChange, isWizardCompleted, data, nodeId, onChange, validationAttempted, validationFailed, performValidation]);

  return (
    <div className="flex flex-col h-full">
      {/* Wizard Header */}
      <div className="px-6 py-4 bg-gradient-to-r from-orange-50 to-red-50 border-b border-gray-200 dark:border-gray-700 dark:from-orange-900/20 dark:to-red-900/20">
        <div className="flex justify-between items-center mb-3">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">HubSpot Integration Wizard</h2>
          <div className="flex gap-2 items-center">
            <button
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="flex gap-1 items-center px-3 py-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
            >
              <Settings className="w-4 h-4" />
              {showAdvanced ? 'Hide' : 'Show'} Advanced
            </button>
          </div>
        </div>
        
                 {/* Progress Indicator */}
         <div className="flex gap-2 items-center">
           {[1, 2, 3].map((step, index) => (
             <div key={step} className="flex items-center">
               <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all ${
                 currentStep === step
                   ? 'bg-orange-600 text-white shadow-lg scale-110'
                   : currentStep > step
                   ? 'bg-green-500 text-white'
                   : 'bg-gray-300 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
               }`}>
                 {currentStep > step ? '✓' : step}
               </div>
               {index < 2 && (
                 <div className={`w-8 h-1 mx-1 rounded-full transition-all ${
                   currentStep > step ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
                 }`} />
               )}
             </div>
           ))}
         </div>
         
         {/* Step Labels */}
         <div className="flex justify-between items-center mt-2 text-xs text-gray-600 dark:text-gray-400">
           <span className={currentStep === 1 ? 'font-medium text-orange-600 dark:text-orange-400' : ''}>API & Operation</span>
           <span className={currentStep === 2 ? 'font-medium text-orange-600 dark:text-orange-400' : ''}>Configure Request</span>
           <span className={currentStep === 3 ? 'font-medium text-orange-600 dark:text-orange-400' : ''}>Response Handling</span>
         </div>
      </div>

      {/* Wizard Content */}
      <div className="overflow-auto flex-1 p-6">
        {/* Validation Results */}
        {(isValidating || lastValidationResult) && (
          <ValidationResultDisplay
            errors={lastValidationResult?.errors || []}
            isValidating={isValidating}
            onDismiss={clearValidationResult}
          />
        )}

        {/* Step 1: API & Operation Selection */}
        {currentStep === 1 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Select API & Operation</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Choose the HubSpot API and operation you want to perform
              </p>
            </div>

            {/* API Type Selection */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">API Type</label>
                <select
                  value={config.apiType}
                  onChange={(e) => handleConfigChange({ apiType: e.target.value, operation: Object.keys(HUBSPOT_API_TYPES[e.target.value as keyof typeof HUBSPOT_API_TYPES]?.operations || {})[0] })}
                  className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {Object.entries(HUBSPOT_API_TYPES).map(([key, apiConfig]) => (
                    <option key={key} value={key}>{apiConfig.label}</option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Operation</label>
                <select
                  value={config.operation}
                  onChange={(e) => handleConfigChange({ operation: e.target.value })}
                  className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                >
                  {Object.entries(getCurrentApiConfig()?.operations || {}).map(([key, operation]) => (
                    <option key={key} value={key}>{operation.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Generated Endpoint Preview */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <h4 className="mb-2 text-sm font-medium text-blue-900 dark:text-blue-200">📡 Generated Endpoint</h4>
              <div className="space-y-1 text-sm text-blue-800 dark:text-blue-300">
                <div><strong>Method:</strong> {config.method}</div>
                <div><strong>Endpoint:</strong> {config.endpoint}</div>
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Configure Request */}
        {currentStep === 2 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Configure Request</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Map your data to HubSpot {getCurrentApiConfig()?.label} properties
              </p>
            </div>

            {/* Upstream Context Display */}
            {getUpstreamFields().length > 0 && (
              <div className="p-4 bg-green-50 rounded-lg border border-green-200 dark:bg-green-900/20 dark:border-green-800">
                <h4 className="mb-3 font-medium text-green-900 dark:text-green-200">🔗 Available Context</h4>
                <div className="grid grid-cols-2 gap-2 md:grid-cols-3">
                  {getUpstreamFields().slice(0, 9).map(field => (
                    <div
                      key={field.id}
                      className="flex justify-between items-center p-2 bg-white rounded border border-green-200 dark:bg-gray-800 dark:border-green-700"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-medium text-green-900 truncate dark:text-green-100">{field.label}</div>
                        <div className="font-mono text-xs text-green-600 truncate dark:text-green-400">{`{{${field.id}}}`}</div>
                      </div>
                      <button
                        onClick={() => navigator.clipboard.writeText(`{{${field.id}}}`)}
                        className="p-1 ml-2 text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-200"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Property Mapping Table */}
            {config.apiType !== 'forms' && config.operation !== 'search' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900 dark:text-white">Property Mapping</h4>
                  <button
                    onClick={addPropertyMapping}
                    className="flex gap-2 items-center px-3 py-2 text-white bg-orange-600 rounded-lg hover:bg-orange-700"
                  >
                    <Plus className="w-4 h-4" />
                    Add Property
                  </button>
                </div>

                <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-800">
                      <tr>
                        <th className="px-4 py-3 text-sm font-medium text-left text-gray-700 dark:text-gray-300">HubSpot Property</th>
                        <th className="px-4 py-3 text-sm font-medium text-left text-gray-700 dark:text-gray-300">Value</th>
                        <th className="px-4 py-3 text-sm font-medium text-left text-gray-700 dark:text-gray-300">Preview</th>
                        <th className="px-4 py-3 w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {propertyMappings.map((mapping, index) => (
                        <tr key={index} className="bg-white dark:bg-gray-900">
                          <td className="px-4 py-3">
                            <select
                              value={mapping.hubspotProperty}
                              onChange={(e) => updatePropertyMapping(index, { hubspotProperty: e.target.value })}
                              className="px-2 py-1 w-full text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            >
                              <option value="">Select property...</option>
                              {getCurrentApiConfig()?.properties.map(prop => (
                                <option key={prop.name} value={prop.name}>
                                  {prop.label} {prop.required ? '*' : ''}
                                </option>
                              ))}
                            </select>
                          </td>
                          <td className="px-4 py-3">
                            <VariablePicker
                              nodeId={nodeId}
                              value={mapping.tokenValue}
                              onChange={(value) => updatePropertyMapping(index, { tokenValue: value })}
                              placeholder="{{form.field}} or static value"
                              className="w-full"
                            />
                          </td>
                          <td className="px-4 py-3">
                            <span className="font-mono text-sm text-gray-600 dark:text-gray-400">
                              {mapping.resolvedPreview || '(empty)'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => removePropertyMapping(index)}
                              className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-200"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                      {propertyMappings.length === 0 && (
                        <tr>
                          <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                            No property mappings added. Click "Add Property" to get started.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Search Configuration for Search Operations */}
            {config.operation === 'search' && (
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium text-gray-900 dark:text-white">Search Configuration</h4>
                </div>
                
                {/* Simple Search Filters */}
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Search Filters</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Add filters to narrow down your search results
                  </p>
                  
                  {/* Deal Stage Filter */}
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Deal Stage</label>
                    <select
                      value={config.dealStage || ''}
                      onChange={(e) => handleConfigChange({ dealStage: e.target.value })}
                      className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Any Stage</option>
                      <option value="appointmentscheduled">Appointment Scheduled</option>
                      <option value="qualifiedtobuy">Qualified to Buy</option>
                      <option value="presentationscheduled">Presentation Scheduled</option>
                      <option value="contractsent">Contract Sent</option>
                      <option value="closedwon">Closed Won</option>
                      <option value="closedlost">Closed Lost</option>
                    </select>
                  </div>

                  {/* Amount Range Filter */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Min Amount</label>
                      <VariablePicker
                        nodeId={nodeId}
                        value={config.minAmount || ''}
                        onChange={(value) => handleConfigChange({ minAmount: value })}
                        placeholder="0 or {{minAmount}}"
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Max Amount</label>
                      <VariablePicker
                        nodeId={nodeId}
                        value={config.maxAmount || ''}
                        onChange={(value) => handleConfigChange({ maxAmount: value })}
                        placeholder="1000000 or {{maxAmount}}"
                        className="w-full"
                      />
                    </div>
                  </div>

                  {/* Close Date Filter */}
                  <div className="space-y-2">
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Close Date</label>
                    <select
                      value={config.closeDateFilter || ''}
                      onChange={(e) => handleConfigChange({ closeDateFilter: e.target.value })}
                      className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="">Any Date</option>
                      <option value="this_month">This Month</option>
                      <option value="next_month">Next Month</option>
                      <option value="this_quarter">This Quarter</option>
                      <option value="next_quarter">Next Quarter</option>
                      <option value="this_year">This Year</option>
                      <option value="custom">Custom Range</option>
                    </select>
                  </div>

                  {/* Custom Date Range */}
                  {config.closeDateFilter === 'custom' && (
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">From Date</label>
                        <VariablePicker
                          nodeId={nodeId}
                          value={config.closeDateFrom || ''}
                          onChange={(value) => handleConfigChange({ closeDateFrom: value })}
                          placeholder="2024-01-01 or {{startDate}}"
                          className="w-full"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">To Date</label>
                        <VariablePicker
                          nodeId={nodeId}
                          value={config.closeDateTo || ''}
                          onChange={(value) => handleConfigChange({ closeDateTo: value })}
                          placeholder="2024-12-31 or {{endDate}}"
                          className="w-full"
                        />
                      </div>
                    </div>
                  )}
                </div>

                {/* Properties to Return */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Properties to Return</label>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Select which deal properties to include in results
                  </p>
                  <div className="grid grid-cols-2 gap-2">
                    {['dealname', 'amount', 'dealstage', 'closedate', 'pipeline', 'dealtype', 'hs_owner_id', 'hs_deal_stage_probability'].map(prop => (
                      <label key={prop} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={config.searchProperties?.includes(prop) || false}
                          onChange={(e) => {
                            const currentProps = config.searchProperties || [];
                            const newProps = e.target.checked
                              ? [...currentProps, prop]
                              : currentProps.filter(p => p !== prop);
                            handleConfigChange({ searchProperties: newProps });
                          }}
                          className="rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                        />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{prop}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Result Limit */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Result Limit</label>
                  <input
                    type="number"
                    value={config.limit || 10}
                    onChange={(e) => handleConfigChange({ limit: parseInt(e.target.value) || 10 })}
                    min="1"
                    max="100"
                    className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    placeholder="10"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Maximum number of results to return (1-100)
                  </p>
                </div>

                {/* Sort Options */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Sort Results By</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Sort Field</label>
                      <select
                        value={config.sortField || 'createdate'}
                        onChange={(e) => handleConfigChange({ sortField: e.target.value })}
                        className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="createdate">Creation Date</option>
                        <option value="closedate">Close Date</option>
                        <option value="amount">Amount</option>
                        <option value="dealname">Deal Name</option>
                        <option value="dealstage">Deal Stage</option>
                      </select>
                    </div>
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">Direction</label>
                      <select
                        value={config.sortDirection || 'DESCENDING'}
                        onChange={(e) => handleConfigChange({ sortDirection: e.target.value })}
                        className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="DESCENDING">Newest First</option>
                        <option value="ASCENDING">Oldest First</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Form ID Configuration for Forms API */}
            {config.apiType === 'forms' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Form ID</label>
                  <VariablePicker
                    nodeId={nodeId}
                    value={config.formId || ''}
                    onChange={(value) => handleConfigChange({ formId: value })}
                    placeholder="HubSpot form ID {{form.id}} or static value"
                    className="w-full"
                  />
                </div>
              </div>
            )}

            {/* Object ID for Update/Get operations */}
            {(config.operation === 'update' || config.operation === 'get') && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Object ID</label>
                <VariablePicker
                  nodeId={nodeId}
                  value={config.objectId || ''}
                  onChange={(value) => handleConfigChange({ objectId: value })}
                  placeholder="HubSpot object ID {{contact.id}} or static value"
                  className="w-full"
                />
              </div>
            )}

            {/* Custom configuration for Custom API */}
            {config.apiType === 'custom' && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">HTTP Method</label>
                    <select
                      value={config.method}
                      onChange={(e) => handleConfigChange({ method: e.target.value })}
                      className="px-3 py-2 w-full bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    >
                      {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map(method => (
                        <option key={method} value={method}>{method}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Custom Endpoint</label>
                    <VariablePicker
                      nodeId={nodeId}
                      value={config.endpoint || ''}
                      onChange={(value) => handleConfigChange({ endpoint: value })}
                      placeholder="/crm/v3/objects/contacts"
                      className="w-full"
                    />
                  </div>
                </div>

                                 <div className="space-y-2">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Request Body (JSON)</label>
                   <textarea
                     value={config.customBody || '{}'}
                     onChange={(e) => handleConfigChange({ customBody: e.target.value })}
                     className="w-full h-32 px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md resize-none font-mono"
                     placeholder="{}"
                   />
                 </div>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Response Handling */}
        {currentStep === 3 && (
          <div className="space-y-6">
            <div className="text-center">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Response Handling</h3>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Configure how the response will be stored and accessed by downstream nodes
              </p>
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Store Response As <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.storeAs}
                  onChange={(e) => handleConfigChange({ storeAs: e.target.value })}
                  placeholder="hubspotContact"
                  className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  This creates a variable accessible as {'{{' + (config.storeAs || 'variableName') + '.data.id}}'} in downstream nodes
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Extract Path (Optional)
                </label>
                <input
                  type="text"
                  value={config.extractPath || ''}
                  onChange={(e) => handleConfigChange({ extractPath: e.target.value })}
                  placeholder="data.properties"
                  className="px-3 py-2 w-full text-gray-900 bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  JSONPath to extract specific data from the response
                </p>
              </div>

                             {/* Response Fields Configuration */}
               <div className="space-y-3">
                 <div className="flex items-center justify-between">
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                     Response Fields
                   </label>
                   <div className="flex gap-2">
                     <button
                       type="button"
                       onClick={() => setShowJsonPaste(!showJsonPaste)}
                       className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200 dark:text-gray-400 dark:bg-gray-800 dark:hover:bg-gray-700"
                     >
                       {showJsonPaste ? 'Hide' : 'Paste JSON'}
                     </button>
                     <button
                       type="button"
                       onClick={() => handleConfigChange({ 
                         responseFields: [...config.responseFields, { key: '', path: '', type: 'string' }] 
                       })}
                       className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
                     >
                       <Plus className="w-3 h-3 mr-1" />
                       Add Field
                     </button>
                   </div>
                 </div>
                 
                 <p className="text-xs text-gray-500 dark:text-gray-400">
                   Extract specific fields from the response and make them available as variables
                 </p>

                 {/* JSON Paste Section */}
                 {showJsonPaste && (
                   <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                     <div className="space-y-3">
                       <div className="flex items-center justify-between">
                         <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300">Paste JSON Response</h5>
                         <div className="flex gap-2">
                           <button
                             type="button"
                             onClick={previewExtractedFields}
                             disabled={!jsonPasteText.trim()}
                             className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-md hover:bg-blue-200 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/30"
                           >
                             Preview
                           </button>
                           <button
                             type="button"
                             onClick={extractFieldsFromJson}
                             disabled={!jsonPasteText.trim()}
                             className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                           >
                             Extract Fields
                           </button>
                         </div>
                       </div>
                       <p className="text-xs text-gray-500 dark:text-gray-400">
                         Paste a JSON response to automatically extract available fields and their paths
                       </p>
                       
                       {/* Example JSON */}
                       <div className="text-xs text-gray-500 dark:text-gray-400">
                         <details className="cursor-pointer">
                           <summary className="hover:text-gray-700 dark:hover:text-gray-300">📋 Show example JSON</summary>
                           <div className="mt-2 p-2 bg-gray-100 dark:bg-gray-700 rounded border font-mono text-xs">
                             <pre className="whitespace-pre-wrap">{`{
  "id": "12345",
  "properties": {
    "email": "john@example.com",
    "firstname": "John",
    "lastname": "Doe"
  },
  "createdAt": "2024-01-01T00:00:00Z",
  "status": "active"
}`}</pre>
                           </div>
                         </details>
                       </div>
                       
                       <textarea
                         value={jsonPasteText}
                         onChange={(e) => setJsonPasteText(e.target.value)}
                         placeholder="Paste your JSON response here..."
                         className="w-full h-32 px-3 py-2 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md resize-none font-mono"
                       />
                       {jsonPasteError && (
                         <div className="text-xs text-red-600 dark:text-red-400">
                           {jsonPasteError}
                         </div>
                       )}
                       
                       {/* Preview of extracted fields */}
                       {jsonPreviewFields.length > 0 && (
                         <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
                           <h6 className="text-xs font-medium text-blue-900 dark:text-blue-200 mb-2">
                             Preview of {jsonPreviewFields.length} fields that will be extracted:
                           </h6>
                           <div className="space-y-1 max-h-32 overflow-y-auto">
                             {jsonPreviewFields.slice(0, 10).map((field, index) => (
                               <div key={index} className="text-xs text-blue-800 dark:text-blue-300">
                                 <span className="font-mono">{field.key}</span> ← <span className="text-blue-600">{field.path}</span> ({field.type})
                               </div>
                             ))}
                             {jsonPreviewFields.length > 10 && (
                               <div className="text-xs text-blue-600 dark:text-blue-400">
                                 ... and {jsonPreviewFields.length - 10} more fields
                               </div>
                             )}
                           </div>
                         </div>
                       )}
                     </div>
                   </div>
                 )}

                {config.responseFields.length === 0 ? (
                  <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                    No response fields configured
                  </div>
                ) : (
                  <div className="space-y-3">
                    {config.responseFields.map((field, index) => (
                      <div key={index} className="grid grid-cols-12 gap-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                        <div className="col-span-4">
                          <input
                            type="text"
                            value={field.key}
                            onChange={(e) => {
                              const newFields = [...config.responseFields];
                              newFields[index] = { ...field, key: e.target.value };
                              handleConfigChange({ responseFields: newFields });
                            }}
                            placeholder="Variable name"
                            className="px-2 py-1 w-full text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                          />
                        </div>
                        <div className="col-span-5">
                          <input
                            type="text"
                            value={field.path}
                            onChange={(e) => {
                              const newFields = [...config.responseFields];
                              newFields[index] = { ...field, path: e.target.value };
                              handleConfigChange({ responseFields: newFields });
                            }}
                            placeholder="JSON path (e.g., data.id)"
                            className="px-2 py-1 w-full text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                          />
                        </div>
                        <div className="col-span-2">
                          <select
                            value={field.type}
                            onChange={(e) => {
                              const newFields = [...config.responseFields];
                              newFields[index] = { ...field, type: e.target.value };
                              handleConfigChange({ responseFields: newFields });
                            }}
                            className="px-2 py-1 w-full text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded"
                          >
                            <option value="string">String</option>
                            <option value="number">Number</option>
                            <option value="boolean">Boolean</option>
                            <option value="object">Object</option>
                            <option value="array">Array</option>
                          </select>
                        </div>
                        <div className="col-span-1">
                          <button
                            type="button"
                            onClick={() => {
                              const newFields = config.responseFields.filter((_, i) => i !== index);
                              handleConfigChange({ responseFields: newFields });
                            }}
                            className="p-1 text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Preview Available Tokens */}
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 dark:bg-blue-900/20 dark:border-blue-800">
              <h4 className="mb-3 font-medium text-blue-900 dark:text-blue-200">🔗 Available After Execution</h4>
              <div className="space-y-2 text-sm text-blue-800 dark:text-blue-300">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-2">
                  <div>
                    <strong>Response Data:</strong>
                    <div className="mt-1 space-y-1 font-mono text-xs">
                      <div>{`{{${config.storeAs || 'response'}.data.id}}`}</div>
                      <div>{`{{${config.storeAs || 'response'}.statusCode}}`}</div>
                      <div>{`{{${config.storeAs || 'response'}.data.properties}}`}</div>
                    </div>
                  </div>
                  <div>
                    <strong>Extracted Data:</strong>
                    <div className="mt-1 space-y-1 font-mono text-xs">
                      {config.extractPath ? (
                        <div>{`{{${config.storeAs || 'response'}.extracted}}`}</div>
                      ) : (
                        <div className="text-gray-500">Set extract path to enable</div>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Response Fields Preview */}
                {config.responseFields.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-blue-200 dark:border-blue-700">
                    <strong>Response Fields:</strong>
                    <div className="mt-1 space-y-1 font-mono text-xs">
                      {config.responseFields.map((field, index) => (
                        <div key={index}>
                          {field.key && field.path ? (
                            <div>{`{{${field.key}}}`} <span className="text-blue-600">← {field.path}</span></div>
                          ) : (
                            <div className="text-gray-500">Configure field {index + 1}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Advanced Configuration */}
            {showAdvanced && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-900 dark:text-white">Advanced Configuration</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Timeout (ms)</label>
                    <input
                      type="number"
                      value={config.timeout || 30000}
                      onChange={(e) => handleConfigChange({ timeout: parseInt(e.target.value) })}
                      className="px-3 py-2 w-full bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Max Retry Attempts</label>
                                         <input
                       type="number"
                       value={config.retryConfig?.maxAttempts || 3}
                       onChange={(e) => handleConfigChange({ 
                         retryConfig: { 
                           maxAttempts: parseInt(e.target.value),
                           delay: config.retryConfig?.delay || 1000
                         } 
                       })}
                       className="px-3 py-2 w-full bg-white rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700"
                     />
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
       </div>
     </div>
   );
}